/**
 * Registration Success Page
 *
 * Shown after a successful signup when email confirmation is required.
 * Displays the email used and instructs the user to click the verification
 * link before they can sign in.
 *
 * Reached via navigate('/registration-success', { state: { email } }) from
 * SignupPage when the backend signup response has needsEmailConfirmation: true.
 *
 * Falls back gracefully if the user lands here directly (no state) — shows a
 * generic message without the email.
 */

import { useLocation, Link, Navigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Mail } from 'lucide-react';
import { PublicHeader } from '../../components/layout/PublicHeader';

interface LocationState {
  email?: string;
  // True when the backend detected an existing unconfirmed account for this
  // email and suppressed a second confirmation send (signup is idempotent on
  // retry so we don't blast users with duplicate emails). The page swaps in
  // a spam-folder reminder for the usual fresh-signup copy.
  alreadyExisted?: boolean;
}

export default function RegistrationSuccessPage() {
  const location = useLocation();
  const state = location.state as LocationState | null;
  const email = state?.email;
  const alreadyExisted = state?.alreadyExisted === true;

  // If someone deep-links to this page without going through signup,
  // bounce them to login. Avoids a confusing dangling state.
  if (!email) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <PublicHeader />
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 mb-4">
              <CheckCircle2 className="h-8 w-8 text-primary" />
            </div>

            <h2 className="text-2xl font-bold text-foreground mb-2">
              {alreadyExisted ? 'Confirmation Still Pending' : 'Registration Successful'}
            </h2>

            <p className="text-sm text-muted-foreground mb-6">
              {alreadyExisted
                ? 'An account with this email already exists and is waiting on email confirmation.'
                : 'Your account has been created. Before you can sign in, please verify your email address.'}
            </p>

            {alreadyExisted && (
              <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-4 mb-4 text-left">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">
                      Check your spam folder
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      We already sent a confirmation email to:
                    </p>
                    <p
                      className="text-sm font-medium text-foreground mt-1 break-all"
                      data-testid="duplicate-email-address"
                    >
                      {email}
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      To avoid duplicate emails, we didn't send another one — please
                      check your inbox (and spam/junk folder) for the original message.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-md border border-border bg-muted/40 p-4 mb-6 text-left">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground">
                    {alreadyExisted ? 'Original email recipient' : 'Check your inbox'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {alreadyExisted
                      ? 'The verification link was sent to:'
                      : 'We sent a verification link to:'}
                  </p>
                  <p
                    className="text-sm font-medium text-foreground mt-1 break-all"
                    data-testid="verification-email-address"
                  >
                    {email}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Click the link in that email, then come back here to sign in.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-xs text-muted-foreground mb-6">
              {alreadyExisted
                ? "Still can't find it? Wait a few minutes for delivery, then check spam. If it's truly missing, contact support — submitting the form again won't send a new email."
                : "Didn't get it? Check your spam folder. If it's still missing, return to the sign-up page and try again — the address might have been mistyped."}
            </p>

            <Link
              to="/login"
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Continue to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
